import { useState } from "react";
import { useDispatch } from "react-redux";
import lodash from "lodash";

import { Button } from "@lightspeed/flame/Button";
import { IconEdit } from "@lightspeed/flame/Icon/Edit";
import styled from "@emotion/styled";

import Select from "react-select";

import { setAlert } from "../../../redux/slices/uiSlice";

import { useSelector } from "react-redux";
import { uiSelector } from "../../../redux/slices/uiSlice";

const TdStyled = styled.td`
  padding: 0 8px !important;
  height: 0 !important;
  background: ${({ sectionBackground }) => sectionBackground || "transparent"};
  border-right: ${({ dividerAfter, dividerColor }) =>
    dividerAfter ? `3px solid ${dividerColor || "#88aef5"}` : "none"};
`;

const SetupButton = styled(Button)`
  min-width: 54px;
  padding: 3px 8px;
  font-size: 12px;
  line-height: 1.2;
  opacity: 0.82;
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(17, 24, 39, 0.45);
`;

const ModalPanel = styled.div`
  width: min(460px, 100%);
  border-radius: 6px;
  padding: 22px;
  background: ${({ themeMode }) => (themeMode === "flame" ? "#fff" : "#242424")};
  color: ${({ themeMode }) => (themeMode === "flame" ? "#333" : "#fff")};
  box-shadow: 0 18px 44px rgba(17, 24, 39, 0.25);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
`;

const ModalCloseButton = styled.button`
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 24px;
  line-height: 1;
`;

const FieldGroup = styled.div`
  display: grid;
  gap: 8px;
  margin-bottom: 16px;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 700;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`;

const TAX_FIELD_KEYS = {
  gst: {
    id: ["gst_tax_id", "gst_id", "tax_gst_id", "category_gst_id"],
    name: ["gst_name", "tax_gst_name", "category_gst_name"],
  },
  pst: {
    id: ["pst_tax_id", "pst_id", "tax_pst_id", "category_pst_id"],
    name: ["pst_name", "tax_pst_name", "category_pst_name"],
  },
};

const findFirstValue = (source, keys) => {
  const key = keys.find((candidate) => source[candidate] !== undefined);
  return key ? source[key] : "";
};

const buildSelectedOption = (item, options, field) => {
  const idValue = findFirstValue(item, TAX_FIELD_KEYS[field].id);
  const nameValue = findFirstValue(item, TAX_FIELD_KEYS[field].name);
  const value = idValue || nameValue;

  return (
    options.find((option) => String(option.value) === String(idValue)) ||
    options.find((option) => String(option.label) === String(nameValue)) ||
    (value ? { value, label: nameValue || value } : null) ||
    null
  );
};

const getPmsIdFieldName = (nameField) =>
  nameField === "pms_name" ? "pms_id" : nameField.replace("pms_name", "pms_id");


const buildTaxOptions = (selectedOption, options) => {
  if (
    !selectedOption ||
    options.some((option) => String(option.value) === String(selectedOption.value))
  ) {
    return options;
  }

  return [selectedOption, ...options];
};

const hasTaxMapping = (item) =>
  Boolean(
    findFirstValue(item, TAX_FIELD_KEYS.gst.id) ||
      findFirstValue(item, TAX_FIELD_KEYS.gst.name) ||
      findFirstValue(item, TAX_FIELD_KEYS.pst.id) ||
      findFirstValue(item, TAX_FIELD_KEYS.pst.name)
  );

const Row = ({
  item,
  editable,
  updateAction,
  rowKeys,
  editableOptions,
  taxCategoryOptions = [],
  editableItemizerNames = {},
  headerData = [],
}) => {
  const [edit, editSet] = useState(false);
  const [value, valueSet] = useState({});
  const [taxModalOpen, taxModalOpenSet] = useState(false);
  const [taxValues, taxValuesSet] = useState({
    gst: buildSelectedOption(item, taxCategoryOptions, "gst"),
    pst: buildSelectedOption(item, taxCategoryOptions, "pst"),
  });
  const taxMappingExists = hasTaxMapping(item);
  const dispatch = useDispatch();
  const { theme } = useSelector(uiSelector);

  const customStyle = {
    option: (base) => ({
      ...base,
      backgroundColor: theme === "flame" ? "#fff" : "#333",
      color: theme === "flame" ? "#333" : "#fff",
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: theme === "flame" ? "#fff" : "#333",
      color: theme === "flame" ? "#333" : "#fff",
    }),
    control: (base) => ({
      ...base,
      backgroundColor: theme === "flame" ? "#fff" : "#333",
      color: theme === "flame" ? "#333" : "#fff",
    }),
    singleValue: (base) => ({
      ...base,
      backgroundColor: theme === "flame" ? "#fff" : "#333",
      color: theme === "flame" ? "#333" : "#fff",
    }),
    input: (base) => ({
      ...base,
      color: theme === "flame" ? "#333" : "#fff",
    }),
  };

  const showSuccessAlert = () => {
    dispatch(
      setAlert({
        type: "success",
        title: "Success",
        text: "Task completed successfuly",
      })
    );
    setTimeout(() => {
      dispatch(
        setAlert({
          type: "",
          title: "",
          text: "",
        })
      );
    }, 3000);
  };

  const onUpdate = () => {
    if (lodash.isEmpty(value)) return editSet(false);

    let newItem = { ...item };

    if (value.category_type) {
      newItem.category_type = value.category_type.label;
    }

    Object.keys(value)
      .filter((key) => key.startsWith("pms_name"))
      .forEach((key) => {
        newItem[key] = value[key].label;

        if (key.startsWith("pms_name")) {
          newItem[getPmsIdFieldName(key)] = value[key].value;
        }

        if (editableItemizerNames[key]) {
          newItem.Itemizer = editableItemizerNames[key];
        }
      });

    editSet(false);
    updateAction(newItem).then(() => {
      showSuccessAlert();
    });
  };

  const onOpenTaxModal = () => {
    taxValuesSet({
      gst: buildSelectedOption(item, taxCategoryOptions, "gst"),
      pst: buildSelectedOption(item, taxCategoryOptions, "pst"),
    });
    taxModalOpenSet(true);
  };

  const onSaveTaxCategory = () => {
    const newItem = {
      ...item,
      gst_tax_id: taxValues.gst?.value ?? "",
      pst_tax_id: taxValues.pst?.value ?? "",
      gst_id: taxValues.gst?.value ?? "",
      gst_name: taxValues.gst?.label ?? "",
      pst_id: taxValues.pst?.value ?? "",
      pst_name: taxValues.pst?.label ?? "",
    };

    taxModalOpenSet(false);
    updateAction(newItem).then(() => {
      showSuccessAlert();
    });
  };

  const onEdit = () => editSet(true);

  return (
    <>
      <tr
        onDoubleClick={onEdit}
        className={`${theme === "flame" ? "" : "dark-table-row"}`}
      >
        {rowKeys.map((i, index) => {
          const columnMeta = headerData[index] || {};
          const cellProps = {
            sectionBackground: columnMeta.sectionBackground,
            dividerAfter: columnMeta.dividerAfter,
            dividerColor: columnMeta.dividerColor,
          };

          if (editable.includes(i) && edit) {
            return (
              <TdStyled key={i} {...cellProps}>
                <Select
                  styles={customStyle}
                  defaultValue={{
                    label: item[i],
                    value: item[i],
                  }}
                  value={value[i]}
                  onChange={(e) =>
                    valueSet((old) => {
                      if (item[i] === e.label) {
                        const tmpOld = { ...old };
                        delete tmpOld[i];
                        return tmpOld;
                      }
                      return { ...old, [i]: e };
                    })
                  }
                  options={editableOptions[i]}
                />
              </TdStyled>
            );
          }
          if (value[i]) {
            return (
              <TdStyled key={i} {...cellProps}>
                {value[i].label}
              </TdStyled>
            );
          }
          return (
            <TdStyled key={i} {...cellProps}>
              {item[i]}
            </TdStyled>
          );
        })}

        <TdStyled
          sectionBackground={headerData[rowKeys.length]?.sectionBackground}
          dividerAfter={headerData[rowKeys.length]?.dividerAfter}
          dividerColor={headerData[rowKeys.length]?.dividerColor}
        >
          <SetupButton onClick={onOpenTaxModal}>
            {taxMappingExists ? "Tax Map*" : "Tax Map"}
          </SetupButton>
        </TdStyled>

        {edit ? (
          <TdStyled
            sectionBackground={headerData[rowKeys.length + 1]?.sectionBackground}
            dividerAfter={headerData[rowKeys.length + 1]?.dividerAfter}
            dividerColor={headerData[rowKeys.length + 1]?.dividerColor}
          >
            <Button onClick={onUpdate}>Save</Button>
            <Button
              onClick={() => {
                valueSet({});
                editSet(false);
              }}
            >
              Cancel
            </Button>
          </TdStyled>
        ) : (
          <TdStyled
            sectionBackground={headerData[rowKeys.length + 1]?.sectionBackground}
            dividerAfter={headerData[rowKeys.length + 1]?.dividerAfter}
            dividerColor={headerData[rowKeys.length + 1]?.dividerColor}
          >
            <Button onClick={onEdit}>
              <IconEdit baseColor={theme === "flame" ? "#333" : "#fff"} />
            </Button>
          </TdStyled>
        )}
      </tr>
      {taxModalOpen && (
        <ModalBackdrop onClick={() => taxModalOpenSet(false)}>
          <ModalPanel
            themeMode={theme}
            onClick={(event) => event.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>Taxes by Category</ModalTitle>
              <ModalCloseButton
                type="button"
                aria-label="Close"
                onClick={() => taxModalOpenSet(false)}
              >
                X
              </ModalCloseButton>
            </ModalHeader>
            <FieldGroup>
              <FieldLabel>GST</FieldLabel>
              <Select
                styles={customStyle}
                options={buildTaxOptions(taxValues.gst, taxCategoryOptions)}
                value={taxValues.gst}
                onChange={(option) =>
                  taxValuesSet((old) => ({ ...old, gst: option }))
                }
                isClearable
                placeholder="Select PMS Name"
              />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>PST</FieldLabel>
              <Select
                styles={customStyle}
                options={buildTaxOptions(taxValues.pst, taxCategoryOptions)}
                value={taxValues.pst}
                onChange={(option) =>
                  taxValuesSet((old) => ({ ...old, pst: option }))
                }
                isClearable
                placeholder="Select PMS Name"
              />
            </FieldGroup>
            <ModalActions>
              <Button onClick={() => taxModalOpenSet(false)}>Cancel</Button>
              <Button onClick={onSaveTaxCategory}>Save</Button>
            </ModalActions>
          </ModalPanel>
        </ModalBackdrop>
      )}
    </>
  );
};

export default Row;




